/** What the app knows about the signed-in account at a given moment. */
export type SentryIdentity = {
  id: string;
  email?: string | null;
  username?: string | null;
};

/**
 * The Sentry user for `identity`. The same account keeps what is already known
 * about it, so the sign-in (id and email, known at start-up) and the profile
 * (username, known once `me` loads) add up whichever comes second. A different
 * account starts afresh. A missing value never erases a known one.
 */
export function identifiedUser<Known extends { id?: string | number }>(
  known: Known | null | undefined,
  identity: SentryIdentity,
) {
  return {
    ...(known?.id === identity.id ? known : undefined),
    id: identity.id,
    ...(identity.email ? { email: identity.email } : undefined),
    ...(identity.username ? { username: identity.username } : undefined),
  };
}

/** Whether Sentry holds a user. A cleared one keeps its keys, all undefined. */
export function hasUser(known: object | null | undefined) {
  return Object.values(known ?? {}).some((value) => value !== undefined);
}

/** Whether setting `next` over `known` would change anything Sentry sends. */
export function isSameUser(
  known: object | null | undefined,
  next: object,
) {
  if (!known) return false;
  const before = known as Record<string, unknown>;
  const after = next as Record<string, unknown>;
  const keys = Object.keys(after);
  return (
    keys.length === Object.keys(before).length &&
    keys.every((key) => before[key] === after[key])
  );
}
